import React from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { fillArray } from 'lib/arrayUtils'
import BodyText from 'components/Luxkit/Typography/BodyText'
import FormatCurrency from 'components/Common/FormatCurrency'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import Heading from 'components/Luxkit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'

const Container = styled.div`
  gap: ${rem(2)};
  display: flex;
  cursor: pointer;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  padding: ${rem(8)} ${rem(12)};

  width: ${rem(144)};
  height: ${rem(88)};

  ${mediaQueryUp.tablet} {
    height: ${rem(58)};
  }

  border-radius: ${props => props.theme.borderRadius.medium};
  background: ${props => props.theme.palette.neutral.eight};
  border: 1px solid ${props => props.theme.palette.neutral.five};

  &.filter-selected {
    border: ${rem(2)} solid ${props => props.theme.palette.neutral.one};
  }

  &.filter-disabled {
    cursor: default;
    color: ${props => props.theme.palette.neutral.two}
  }

  &:hover {
    opacity: 0.8;

    &.filter-disabled {
      opacity: 1;
      box-shadow: none;
    }
  }
`

const CabinPriceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  ${mediaQueryUp.tablet} {
    flex-direction: row;
    gap: ${rem(4)};
  }
`

const ContentSkeleton = styled(LoadingBox)`
  width: 100%;
  border-radius: ${props => props.theme.borderRadius.large};
  margin: ${rem(4)} 0;
  min-height: ${rem(8)};
`

type Props = {
  cabinType: string
  price?: number
  disabled?: boolean
  selected?: boolean
  onClick: (cabinType: string) => void
}

function CabinFilterTile(props: Props) {
  const { price, disabled, selected, cabinType, onClick } = props

  return (
    <Container
      aria-disabled={disabled}
      onClick={() => !disabled && onClick(cabinType)}
      className={`${selected && 'filter-selected'} ${disabled && 'filter-disabled'}`}>

      {!disabled && (
        <>
          <Heading variant="heading6" as="span">{cabinType}</Heading>
          <CabinPriceWrapper>
            <BodyText variant="small">From{' '}</BodyText>
            <BodyText variant="small" weight="bold">
              <FormatCurrency value={Math.round(price)} format="dynamicDollar"/>
            </BodyText>
          </CabinPriceWrapper>
        </>
      )}

      {disabled && (
        <>
          <Heading variant="heading6" as="span" colour="neutral-four">{cabinType}</Heading>
          <BodyText variant="small" weight="bold" colour="neutral-four">Sold out</BodyText>
        </>
      )}
    </Container>
  )
}

export const CabinFilterTileSkeleton = () => {
  return (
    <>
      {fillArray(4).map((id) => (
        <Container key={id}>
          <ContentSkeleton />
          <ContentSkeleton />
        </Container>
      ))}
    </>
  )
}

export default CabinFilterTile
